'use client'

import { Fragment } from 'react'
import { FormattedDate } from 'react-intl'
import { Cell, Text, Icon } from '@vinted/web-ui'
import classNames from 'classnames'
import {
  CheckCircle24,
  CheckCircleFilled24,
  Redirect24,
  StatusActive24,
} from '@vinted/monochrome-icons'

import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'
import { clickEvent } from '_libs/common/event-tracker/events'

import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { TrackingJourneySummaryDetails } from '@marketplace-web/conversation-shipping/conversation-shipping-data'

type Props = {
  transactionId?: number
  trackingDetails: Array<TrackingJourneySummaryDetails>
}

const Timeline = ({ transactionId, trackingDetails }: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('conversation.tracking_journey_details')

  function trackProviderTrackingUrlClick(carrierId: number, carrierPriority: number) {
    const targetDetails = {
      transaction_id: transactionId,
      carrier_id: carrierId,
      carrier_priority: carrierPriority,
    }

    return () => {
      track(
        clickEvent({
          target: ClickableElement.OpenTrackingPage,
          screen: Screen.TrackShipmentView,
          targetDetails: JSON.stringify(targetDetails),
        }),
      )
    }
  }

  function renderContent(trackingDetail: TrackingJourneySummaryDetails, isFirstElement: boolean) {
    const textType = isFirstElement ? Text.Type.Title : Text.Type.Body

    return (
      <div className="timeline__content">
        <Cell
          testId={`timeline-content-${trackingDetail.timestamp}`}
          styling={Cell.Styling.Tight}
          theme="transparent"
          title={
            <Text
              as={isFirstElement ? 'h2' : 'span'}
              text={trackingDetail.message}
              type={textType}
              theme="amplified"
            />
          }
          body={
            <Text
              as="span"
              text={
                <FormattedDate
                  value={trackingDetail.timestamp}
                  year="numeric"
                  month="long"
                  day="numeric"
                  hour="numeric"
                  minute="numeric"
                />
              }
            />
          }
          suffix={
            trackingDetail.carrier?.trackingUrl && (
              <a
                href={trackingDetail.carrier.trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="carrier_url"
                title={translate('a11y.link_title')}
                onClick={trackProviderTrackingUrlClick(
                  trackingDetail.carrier.id,
                  trackingDetail.carrier.priority,
                )}
              >
                <Icon
                  name={Redirect24}
                  color={Icon.Color.GreyscaleLevel2}
                  testId="timeline-redirect-icon"
                />
              </a>
            )
          }
        />
      </div>
    )
  }

  function renderIcon(trackingDetail: TrackingJourneySummaryDetails, isFirstElement: boolean) {
    const cellType = trackingDetail.cellType === 'finished'
    const isCurrentIcon = isFirstElement ? StatusActive24 : CheckCircle24

    return <Icon name={cellType ? CheckCircleFilled24 : isCurrentIcon} color={Icon.Color.Primary} />
  }

  return (
    <div className="timeline__wrapper">
      {trackingDetails.map((trackingDetail, index) => {
        const isFirstElement = index === 0
        const isLastElement = trackingDetails.length === index + 1

        return (
          <Fragment key={trackingDetail.timestamp}>
            <div
              className={classNames(
                'timeline__item',
                isFirstElement ? 'timeline__item--active' : null,
              )}
            >
              <div className="timeline__mark">
                {renderIcon(trackingDetail, isFirstElement)}
                {!isLastElement && <div className="timeline__separator" />}
              </div>
              {renderContent(trackingDetail, isFirstElement)}
            </div>
            {!isLastElement && (
              <div className="timeline__connector">
                <div className="timeline__connector-line" />
              </div>
            )}
          </Fragment>
        )
      })}
    </div>
  )
}

export default Timeline
